import { useState } from 'react';
import { rolePermissions } from '../../lib/permissions';
import type { Permission, Role } from '../../types';
import { Ic } from '../mock/primitives';
import { useContent } from '../siteContent';

const roleKeys: Exclude<Role, 'super_admin'>[] = ['restaurant_owner', 'restaurant_manager', 'branch_manager', 'cashier', 'customer_service'];
const defaultRoleText: { key: Exclude<Role, 'super_admin'>; title: string; text: string }[] = [
  { key: 'restaurant_owner', title: 'مالك المطعم', text: 'نظرة شاملة على النظام والفروع والإعدادات' },
  { key: 'restaurant_manager', title: 'مدير المطعم', text: 'إدارة العمليات والاشتراكات والفريق وفق الصلاحيات' },
  { key: 'branch_manager', title: 'مدير الفرع', text: 'متابعة عمليات الفرع والمشتركين والطلبات' },
  { key: 'cashier', title: 'الكاشير', text: 'تنفيذ عمليات الصرف والإجراءات المسموحة بسرعة' },
  { key: 'customer_service', title: 'خدمة العملاء', text: 'متابعة المشتركين والطلبات ومساعدتهم' },
];

// Tools shown in the demo, mapped to the real default permissions in src/lib/permissions.ts.
const tools: { label: string; icon: string; permission: Permission }[] = [
  { label: 'صرف الوجبات', icon: 'scan', permission: 'redemptions.create' },
  { label: 'الاشتراكات', icon: 'card', permission: 'subscriptions.view' },
  { label: 'تجميد الاشتراك', icon: 'card', permission: 'subscriptions.freeze' },
  { label: 'إلغاء الاشتراك', icon: 'close', permission: 'subscriptions.cancel' },
  { label: 'طلبات التوصيل', icon: 'box', permission: 'deliveryOrders.manage' },
  { label: 'إدارة الباقات', icon: 'box', permission: 'plans.manage' },
  { label: 'التقارير', icon: 'chart', permission: 'reports.view' },
  { label: 'إدارة الموظفين', icon: 'users', permission: 'staff.manage' },
  { label: 'الإعدادات', icon: 'settings', permission: 'settings.manage' },
];

const branchFigures = [{ staff: 9, today: 64 }, { staff: 6, today: 41 }, { staff: 4, today: 27 }, { staff: 5, today: 33 }, { staff: 3, today: 19 }];

export function Team() {
  const { team } = useContent();
  const roles = roleKeys.map((key, index) => ({ key, title: team.roles[index]?.title || defaultRoleText[index].title, text: team.roles[index]?.text ?? defaultRoleText[index].text }));
  const branches = (team.branches.length ? team.branches : ['الفرع الرئيسي']).slice(0, branchFigures.length).map((name, index) => ({ name, ...branchFigures[index] }));
  const [role, setRole] = useState(0);
  const [branch, setBranch] = useState(0);
  if (!team.visible) return null;
  const current = roles[role];
  const shown = branches[Math.min(branch, branches.length - 1)];
  const allowed = rolePermissions[current.key];
  const scopedToBranch = current.key !== 'restaurant_owner' && current.key !== 'restaurant_manager';
  return <section className="mx-team" aria-labelledby="team-title">
    <div className="mx-container">
      <header className="mx-section-head mx-section-head-split">
        <h2 id="team-title" className="mx-h2"><span>{team.line1}</span><span>{team.line2}</span></h2>
        <p className="mx-body">{team.body}</p>
      </header>
      <div className="mx-team-grid">
        <fieldset className="mx-roles">
          <legend>اختر الدور</legend>
          {roles.map((item, index) => <label key={item.key} className={`mx-role${role === index ? ' is-on' : ''}`}>
            <input type="radio" name="team-role" checked={role === index} onChange={() => setRole(index)} />
            <span><b>{item.title}</b><small>{item.text}</small></span>
          </label>)}
        </fieldset>
        <div className="mx-team-panel">
          <div className="mx-branch-switch" role="group" aria-label="تبديل الفرع">
            {branches.map((item, index) => <button key={item.name} type="button" aria-pressed={branch === index} onClick={() => setBranch(index)}>{item.name}</button>)}
          </div>
          <div className="mx-branch-stats" aria-live="polite">
            <span><small>الفرع المعروض</small><b>{shown.name}</b></span>
            <span><small>الموظفون</small><b>{shown.staff}</b></span>
            <span><small>وجبات اليوم</small><b>{shown.today}</b></span>
          </div>
          <div className="mx-perms">
            <p className="mx-perms-head"><b>ما يراه {current.title}</b><small>{scopedToBranch ? 'ضمن الفرع المحدد له' : 'على مستوى المطعم وكل الفروع'}</small></p>
            <ul>
              {tools.map(tool => { const on = allowed.includes(tool.permission); return <li key={tool.label} className={on ? 'is-on' : ''}>
                <Ic name={tool.icon} /><span>{tool.label}</span><em>{on ? 'متاح' : 'غير متاح'}</em>
              </li>; })}
            </ul>
            <small className="mx-perms-foot">الصلاحيات الافتراضية لكل دور في ميلنكس، وأسماء الفروع والأرقام توضيحية</small>
          </div>
        </div>
      </div>
    </div>
  </section>;
}
